import { useEffect } from 'react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const ContatoPage = () => {
  useEffect(() => {
    const jsonLdData = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Entre em Contato - Target Software",
      "description": "Entre em contato com a Target Software para conhecer nossas soluções em incentivos comerciais, gestão de parceiros e business intelligence.",
      "url": "https://targetsoftware.com.br/contato",
      "mainEntity": {
        "@type": "Organization",
        "name": "Target Software",
        "description": "Empresa especializada em soluções SaaS para incentivos comerciais, gestão de parceiros e business intelligence.",
        "url": "https://targetsoftware.com.br",
        "logo": "https://targetsoftware.com.br/images/logos/logo-target.png",
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "telephone": "+55-11-91988-0800",
            "contactType": "customer service",
            "availableLanguage": "Portuguese",
            "areaServed": "BR"
          },
          {
            "@type": "ContactPoint",
            "email": "comercial@targetsoftware.com.br",
            "contactType": "sales",
            "availableLanguage": "Portuguese",
            "areaServed": "BR"
          }
        ],
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "BR",
          "addressRegion": "SP",
          "addressLocality": "São Paulo e Sorocaba"
        },
        "sameAs": [
          "https://www.linkedin.com/company/target-software"
        ]
      },
      "potentialAction": {
        "@type": "CommunicateAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://wa.me/5511919880800",
          "inLanguage": "pt-BR",
          "actionPlatform": [
            "http://schema.org/DesktopWebPlatform",
            "http://schema.org/MobileWebPlatform"
          ]
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLdData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const openWhatsApp = () => {
    window.open('https://wa.me/5511919880800', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header startInDarkMode={false} />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#F6F6F6] to-[#E3E3E3] overflow-hidden">
        <div className="absolute inset-0">
          {/* Enhanced Floating Elements */}
          <div className="absolute top-20 left-10 w-2 h-2 bg-[#7d2128] rounded-full animate-pulse opacity-60"></div>
          <div className="absolute top-40 right-20 w-1 h-1 bg-gray-400 rounded-full animate-ping opacity-40"></div>

          {/* New Horizontal Moving Elements */}
          <div className="absolute top-16 left-0 w-4 h-4 bg-[#7d2128]/20 animate-move-right opacity-40"></div>
          <div className="absolute top-32 left-0 w-2 h-2 bg-gray-500/30 animate-move-right-slow opacity-50" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-48 left-0 w-3 h-3 bg-[#7d2128]/30 animate-move-right-fast opacity-60" style={{ animationDelay: '2s' }}></div>

          {/* New Elements Moving from Right */}
          <div className="absolute top-24 right-0 w-4 h-4 bg-gray-600/25 animate-move-left opacity-45"></div>
          <div className="absolute top-40 right-0 w-2 h-2 bg-[#7d2128]/35 animate-move-left-fast opacity-55" style={{ animationDelay: '0.8s' }}></div>
          <div className="absolute bottom-28 right-0 w-3 h-3 bg-gray-500/30 animate-move-left-slow opacity-40" style={{ animationDelay: '1.2s' }}></div>

          {/* Enhanced Vertical Moving Elements */}
          <div className="absolute top-0 left-16 w-6 h-6 bg-[#7d2128]/15 rounded-full animate-move-down opacity-40"></div>
          <div className="absolute top-0 left-32 w-4 h-4 bg-gray-500/20 rounded-full animate-move-down-slow opacity-50" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute top-0 left-48 w-3 h-3 bg-[#7d2128]/25 rounded-full animate-move-down-fast opacity-60" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-0 right-16 w-5 h-5 bg-gray-400/18 rounded-full animate-move-down opacity-35" style={{ animationDelay: '1.5s' }}></div>

          {/* New Elements Moving Up */}
          <div className="absolute bottom-0 left-24 w-5 h-5 bg-gray-500/20 rounded-full animate-move-up opacity-40"></div>
          <div className="absolute bottom-0 left-40 w-3 h-3 bg-[#7d2128]/28 rounded-full animate-move-up-slow opacity-50" style={{ animationDelay: '0.7s' }}></div>
          <div className="absolute bottom-0 right-24 w-4 h-4 bg-gray-400/25 rounded-full animate-move-up-fast opacity-45" style={{ animationDelay: '1.3s' }}></div>

          {/* Rotating Elements */}
          <div className="absolute top-1/4 left-12 w-8 h-8 bg-[#7d2128]/10 animate-rotate-slow opacity-30"></div>
          <div className="absolute top-3/4 right-12 w-6 h-6 bg-gray-500/15 animate-rotate-reverse opacity-40" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-20 w-4 h-4 bg-[#7d2128]/20 animate-rotate-fast opacity-50" style={{ animationDelay: '0.5s' }}></div>

          {/* Floating Geometric Shapes */}
          <div className="absolute top-32 left-1/4 w-3 h-3 bg-[#7d2128]/25 rotate-45 animate-float opacity-45"></div>
          <div className="absolute bottom-32 right-1/4 w-4 h-4 bg-gray-500/20 rotate-45 animate-float-slow opacity-40" style={{ animationDelay: '0.8s' }}></div>

          {/* Additional Circuit-like Elements */}
          <div className="absolute top-20 right-1/3 w-6 h-1 bg-[#7d2128]/30 animate-pulse opacity-40" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute bottom-20 left-1/3 w-8 h-1 bg-gray-500/25 animate-pulse opacity-35" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-8 w-4 h-1 bg-[#7d2128]/40 animate-pulse opacity-50" style={{ animationDelay: '1.5s' }}></div>

          {/* Corner Elements */}
          <div className="absolute top-4 left-4 w-2 h-2 bg-[#7d2128]/20 animate-ping opacity-60" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute top-4 right-4 w-3 h-3 bg-gray-500/15 animate-bounce opacity-40" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 mt-9">
              Entre em <span className="text-[#7d2128]">Contato</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Estamos prontos para transformar seu negócio. Fale conosco e <br/>descubra como nossas soluções podem impulsionar seus resultados.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-stretch">
            {/* WhatsApp Contact */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 border border-green-200 h-full flex flex-col">
              <div className="rounded-2xl p-8 text-center h-full">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-whatsapp-line text-3xl text-white"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Fale Conosco no WhatsApp</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Converse diretamente com nossos especialistas para tirar dúvidas, solicitar demonstrações ou obter uma proposta personalizada.
                </p>
                <button
                  onClick={openWhatsApp}
                  className="inline-flex items-center px-8 py-4 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-all duration-200 hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-xl mr-3"></i>
                  (11) 91988-0800
                </button>
              </div>
            </div>

            {/* Other Contact Methods */}
            <div className="bg-gray-50 rounded-2xl p-8 h-full flex flex-col" style={{ border: '1px solid #e6e9eb' }}>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-customer-service-2-line text-3xl text-red-600"></i>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Outras Formas de Contato</h2>
              </div>
              
              <div className="space-y-4 flex-1 flex flex-col justify-center">
                <div className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-red-600"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">E-mail</p>
                    <p className="text-gray-600 text-sm">comercial@targetsoftware.com.br</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-red-600"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Localização</p>
                    <p className="text-gray-600 text-sm">Unidades em São Paulo e Sorocaba</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-red-600"></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Horário de Atendimento</p>
                    <p className="text-gray-600 text-sm">
                      Segunda a Sexta: 9h às 18h
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-1 gap-12 max-w-6xl mx-auto">
            {/* Formulário de Contato */}
            <div className="bg-gray-50 rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="_form_7"></div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes move-right {
          0% {
            transform: translateX(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
        }

        @keyframes move-right-slow {
          0% {
            transform: translateX(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
        }

        @keyframes move-right-fast {
          0% {
            transform: translateX(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
        }

        @keyframes move-left {
          0% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(-100px);
            opacity: 0;
          }
        }

        @keyframes move-left-slow {
          0% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(-100px);
            opacity: 0;
          }
        }

        @keyframes move-left-fast {
          0% {
            transform: translateX(calc(100vw + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateX(-100px);
            opacity: 0;
          }
        }

        @keyframes move-down {
          0% {
            transform: translateY(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
        }

        @keyframes move-down-slow {
          0% {
            transform: translateY(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
        }

        @keyframes move-down-fast {
          0% {
            transform: translateY(-100px);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
        }

        @keyframes move-up {
          0% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100px);
            opacity: 0;
          }
        }

        @keyframes move-up-slow {
          0% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100px);
            opacity: 0;
          }
        }

        @keyframes move-up-fast {
          0% {
            transform: translateY(calc(100vh + 100px));
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100px);
            opacity: 0;
          }
        }

        @keyframes rotate-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes rotate-reverse {
          from {
            transform: rotate(360deg);
          }
          to {
            transform: rotate(0deg);
          }
        }

        @keyframes rotate-fast {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(45deg);
          }
          50% {
            transform: translateY(-20px) rotate(45deg);
          }
        }

        @keyframes float-slow {
          0%,
          100% {
            transform: translateY(0px) rotate(45deg);
          }
          50% {
            transform: translateY(-15px) rotate(45deg);
          }
        }

        @keyframes float-fast {
          0%,
          100% {
            transform: translateY(0px) rotate(45deg);
          }
          50% {
            transform: translateY(-25px) rotate(45deg);
          }
        }

        .animate-move-right {
          animation: move-right 12s linear infinite;
        }

        .animate-move-right-slow {
          animation: move-right-slow 18s linear infinite;
        }

        .animate-move-right-fast {
          animation: move-right-fast 8s linear infinite;
        }

        .animate-move-left {
          animation: move-left 12s linear infinite;
        }

        .animate-move-left-slow {
          animation: move-left-slow 18s linear infinite;
        }

        .animate-move-left-fast {
          animation: move-left-fast 8s linear infinite;
        }

        .animate-move-down {
          animation: move-down 15s linear infinite;
        }

        .animate-move-down-slow {
          animation: move-down-slow 22s linear infinite;
        }

        .animate-move-down-fast {
          animation: move-down-fast 10s linear infinite;
        }

        .animate-move-up {
          animation: move-up 15s linear infinite;
        }

        .animate-move-up-slow {
          animation: move-up-slow 22s linear infinite;
        }

        .animate-move-up-fast {
          animation: move-up-fast 10s linear infinite;
        }

        .animate-rotate-slow {
          animation: rotate-slow 25s linear infinite;
        }

        .animate-rotate-reverse {
          animation: rotate-reverse 20s linear infinite;
        }

        .animate-rotate-fast {
          animation: rotate-fast 12s linear infinite;
        }

        .animate-float {
          animation: float 5s ease-in-out infinite;
        }

        .animate-float-slow {
          animation: float-slow 7s ease-in-out infinite;
        }

        .animate-float-fast {
          animation: float-fast 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ContatoPage;
